import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { Ticket, Plus, Trash2, Loader2, Tag, Gift } from "lucide-react";

const OFFERS = [
  ["all", "All offers"],
  ["packages", "Website packages"],
  ["plan_unlock", "Business Plan ($99)"],
  ["template", "Website designs"],
  ["concierge_bundle", "AI Concierge bundle"],
];
const EMPTY = { code: "", percent: 25, scope: ["all"], enabled: true, start_date: "", end_date: "", max_uses: "", label: "" };
const INPUT = "w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none";

export function DiscountCodes() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/discount-codes"); setRows(data); }
    catch (e) { toast.error("Could not load discount codes."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allSel = form.scope.includes("all");
  const toggleOffer = (key) => {
    if (key === "all") return set("scope", ["all"]);
    let next = form.scope.filter((s) => s !== "all");
    next = next.includes(key) ? next.filter((s) => s !== key) : [...next, key];
    set("scope", next.length ? next : ["all"]);
  };

  const create = async () => {
    if (!form.code.trim()) { toast.error("Enter a code."); return; }
    setSaving(true);
    try {
      await api.post("/admin/discount-codes", {
        code: form.code.trim().toUpperCase(), percent: Number(form.percent) || 0,
        scope: form.scope, enabled: form.enabled, start_date: form.start_date, end_date: form.end_date,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses), label: form.label.trim(),
      });
      toast.success("Discount code created.");
      setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/discount-codes/${c.id}`, { ...c, enabled: !c.enabled });
      setRows((rs) => rs.map((r) => (r.id === c.id ? { ...r, enabled: !r.enabled } : r)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}?`)) return;
    try { await api.delete(`/admin/discount-codes/${c.id}`); setRows((rs) => rs.filter((r) => r.id !== c.id)); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Loader />;
  const list = rows || [];

  return (
    <div data-testid="discount-codes-section">
      <Head title="Discount Codes" subtitle="Create any code for any offer — set the % off, or make it 100% free (e.g. a free website for training attendees). Customers enter the code at checkout." />

      {/* Create */}
      <div className="border border-white/10 bg-navy-800 p-6 mb-8 max-w-3xl" data-testid="dc-create-card">
        <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><Plus size={17} className="text-gold" /> New code</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Code</label>
            <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="dc-code" placeholder="TRAINING2026" className={`${INPUT} uppercase`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Percent off</label>
            <input type="number" min="0" max="100" value={form.percent} onChange={(e) => set("percent", e.target.value)} data-testid="dc-percent" className={INPUT} />
          </div>
          <div className="flex items-end">
            <button type="button" onClick={() => set("percent", 100)} data-testid="dc-free-btn"
              className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm border transition-colors ${Number(form.percent) === 100 ? "bg-gold text-navy border-gold" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
              <Gift size={14} /> Make it FREE
            </button>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-gray-400 block mb-2">Applies to</label>
          <div className="flex flex-wrap gap-2">
            {OFFERS.map(([key, label]) => {
              const on = key === "all" ? allSel : (!allSel && form.scope.includes(key));
              return (
                <button key={key} type="button" onClick={() => toggleOffer(key)} data-testid={`dc-scope-${key}`}
                  className={`text-xs px-3 py-1.5 border transition-colors ${on ? "bg-gold/15 text-gold border-gold/40" : "border-white/15 text-gray-400 hover:text-gray-200"}`}>{label}</button>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Max uses (optional)</label>
            <input type="number" min="1" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} data-testid="dc-max-uses" placeholder="Unlimited" className={INPUT} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Start (optional)</label>
            <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="dc-start" className={INPUT} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">End (optional)</label>
            <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="dc-end" className={INPUT} />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Internal label (optional)</label>
          <input value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="dc-label" placeholder="e.g. Free website — Aug training cohort" className={INPUT} />
        </div>
        <button onClick={create} disabled={saving} data-testid="dc-create"
          className="mt-5 inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create code
        </button>
      </div>

      {/* List */}
      {list.length === 0 ? <EmptyBox>No discount codes yet — create your first above.</EmptyBox> : (
        <div className="border border-white/10 bg-navy-800 overflow-x-auto" data-testid="dc-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Discount</th>
                <th className="px-4 py-3 font-medium">Applies to</th>
                <th className="px-4 py-3 font-medium">Uses</th>
                <th className="px-4 py-3 font-medium">Live</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-white/5 last:border-0" data-testid={`dc-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-gold flex items-center gap-1.5"><Tag size={13} /> {c.code}</span>
                    {c.label && <div className="text-[11px] text-gray-500 mt-0.5">{c.label}</div>}
                  </td>
                  <td className="px-4 py-3">{c.percent >= 100 ? <span className="text-emerald-300 font-semibold">FREE</span> : <span className="text-white">{c.percent}% off</span>}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{(c.scope || []).map((s) => (OFFERS.find((o) => o[0] === s) || [null, s])[1]).join(", ")}</td>
                  <td className="px-4 py-3 text-gray-400">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(c)} data-testid={`dc-toggle-${c.id}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${c.enabled ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/15" : "text-gray-500 border-white/10"}`}>
                      {c.enabled ? "On" : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(c)} data-testid={`dc-delete-${c.id}`} className="text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
